import React, { useEffect, useState } from "react";
import "./PerformanceAnalytics.css";
import styles from "./PerformanceAnalytics.module.css";

function PerformanceAnalytics({
  campaignId,
  targetViews,
  setCurrentViews,
  setLatestViews,
  setPercentChange,
  setLatestLikes,
  setLatestComments,
  setLikesCommentsPercentChange,
  setEngagementRate,
  setLatestRecordTime,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [groupBy, setGroupBy] = useState("day"); // 'day' or 'hour'
  const [likesCommentsSeries, setLikesCommentsSeries] = useState([]);
  const [localLatestLikes, setLocalLatestLikes] = useState(null);
  const [localLatestComments, setLocalLatestComments] = useState(null);
  // 新增：缓存两种模式的数据
  const [cachedData, setCachedData] = useState({ day: null, hour: null });
  const [cachedLikesComments, setCachedLikesComments] = useState({ day: null, hour: null });

  useEffect(() => {
    // 切换campaignId时重置所有数据相关state，防止串台
    setChartData(null);
    setLikesCommentsSeries([]);
    setLocalLatestLikes(null);
    setLocalLatestComments(null);
    setError(null);
    setCachedData({ day: null, hour: null });
    setCachedLikesComments({ day: null, hour: null });
    setCurrentViews && setCurrentViews(null);
    setLatestViews && setLatestViews(null);
    setPercentChange && setPercentChange(null);
    setLatestLikes && setLatestLikes(null);
    setLatestComments && setLatestComments(null);
    setLikesCommentsPercentChange && setLikesCommentsPercentChange(null);
    setEngagementRate && setEngagementRate(null);
    setLatestRecordTime && setLatestRecordTime(null);
    if (!campaignId) return;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const axios = (await import("axios")).default;
        // 1. 获取campaign-influencer列表
        const ciRes = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/campaign-influencer?campaign_id=${campaignId}`,
        );
        const ciList = ciRes.data?.campaign_influencers || [];
        
        // 2. 并行获取所有post
        const postPromises = ciList.map(ci => 
          axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/post?campaign_influencer_id=${ci.campaign_influencer_id}`
          ).catch(() => ({ data: { posts: [] } }))
        );
        const postResults = await Promise.all(postPromises);
        const allPosts = postResults.flatMap(res => res.data?.posts || []);
        
        // 3. 并行获取所有post的历史数据
        const historyPromises = allPosts.map(post => {
          if (!post.id && !post.post_id) return Promise.resolve(null);
          return axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/post-url-record-history?post_id=${post.id || post.post_id}`
          ).catch(() => ({ data: { post_history_records_list: [] } }));
        });
        
        const historyResults = await Promise.all(historyPromises);
        
        // 保持原有的数据处理逻辑
        const allHistory = {};
        const likesCommentsHistory = {};
        const perPostDayLatest = {};
        const perPostDayLatestLC = {};
        
        historyResults.forEach((histRes, index) => {
          if (!histRes) return;
          const post = allPosts[index];
          const records = histRes.data?.post_history_records_list || [];
          const dayMap = {};
          const dayMapLC = {};
          
          for (const rec of records) {
            let utcString = rec.record_time;
            if (utcString && !utcString.endsWith("Z")) {
              utcString = utcString.replace(" ", "T").replace(/\.\d+$/, "") + "Z";
            }
            const d = utcString ? new Date(utcString) : null;
            if (!d || isNaN(d)) continue;
            const dayKey = d.getFullYear() + "-" + 
              String(d.getMonth() + 1).padStart(2, "0") + "-" + 
              String(d.getDate()).padStart(2, "0");
            
            const playCount = rec.post_metrics_history?.stats?.play_count ?? 
                            rec.post_metrics_history?.stats?.view_count ?? 0;
            const likes = rec.post_metrics_history?.stats?.like_count ?? 
                        rec.post_metrics_history?.stats?.likes ?? 0;
            const comments = rec.post_metrics_history?.stats?.comment_count ?? 
                           rec.post_metrics_history?.stats?.comments ?? 0;
            
            if (!dayMap[dayKey] || new Date(rec.record_time) > new Date(dayMap[dayKey].record_time)) {
              dayMap[dayKey] = { playCount, record_time: rec.record_time };
            }
            if (!dayMapLC[dayKey] || new Date(rec.record_time) > new Date(dayMapLC[dayKey].record_time)) {
              dayMapLC[dayKey] = { value: likes + comments, record_time: rec.record_time };
            }
          }
          
          perPostDayLatest[post.id || post.post_id] = dayMap;
          perPostDayLatestLC[post.id || post.post_id] = dayMapLC;
          
          // 保持原有的hour逻辑
          for (const rec of records) {
            let utcString = rec.record_time;
            if (utcString && !utcString.endsWith("Z")) {
              utcString = utcString.replace(" ", "T").replace(/\.\d+$/, "") + "Z";
            }
            const d = utcString ? new Date(utcString) : null;
            if (!d || isNaN(d)) continue;
            let key;
            if (groupBy === "hour") {
              key = d.getFullYear() + "-" + 
                String(d.getMonth() + 1).padStart(2, "0") + "-" + 
                String(d.getDate()).padStart(2, "0") + " " + 
                String(d.getHours()).padStart(2, "0") + ":00";
              
              const playCount = rec.post_metrics_history?.stats?.play_count ?? 
                              rec.post_metrics_history?.stats?.view_count ?? 0;
              const likes = rec.post_metrics_history?.stats?.like_count ?? 
                          rec.post_metrics_history?.stats?.likes ?? 0;
              const comments = rec.post_metrics_history?.stats?.comment_count ?? 
                             rec.post_metrics_history?.stats?.comments ?? 0;
              
              if (!allHistory[key]) allHistory[key] = 0;
              allHistory[key] += playCount;
              if (!likesCommentsHistory[key]) likesCommentsHistory[key] = 0;
              likesCommentsHistory[key] += likes + comments;
            }
          }
        });

        // 计算 day 模式数据
        const allDaysSet = new Set();
        Object.values(perPostDayLatest).forEach((dayMap) => {
          Object.keys(dayMap).forEach((dayKey) => allDaysSet.add(dayKey));
        });
        const allDays = Array.from(allDaysSet).sort((a, b) => new Date(a) - new Date(b));
        
        const dayData = allDays.map((dayKey) => {
          let sum = 0;
          Object.values(perPostDayLatest).forEach((dayMap) => {
            if (dayMap[dayKey]) sum += dayMap[dayKey].playCount;
          });
          return { date: dayKey, play_count: sum };
        });
        
        const dayLikesCommentsData = allDays.map((dayKey) => {
          let sum = 0;
          Object.values(perPostDayLatestLC).forEach((dayMap) => {
            if (dayMap[dayKey]) sum += dayMap[dayKey].value;
          });
          return { date: dayKey, value: sum };
        });

        // 立即设置 day 模式数据
        setChartData(dayData);
        setLikesCommentsSeries(dayLikesCommentsData);
        setCachedData(prev => ({ ...prev, day: dayData }));
        setCachedLikesComments(prev => ({ ...prev, day: dayLikesCommentsData }));

        // 异步计算 hour 模式数据
        (async () => {
          try {
            const now = new Date();
            const nowHour = new Date(now);
            nowHour.setMinutes(0, 0, 0);
            const hours = [];
            for (let i = 23; i >= 0; i--) {
              const d = new Date(nowHour.getTime() - i * 60 * 60 * 1000);
              const key = d.getFullYear() + "-" + 
                String(d.getMonth() + 1).padStart(2, "0") + "-" + 
                String(d.getDate()).padStart(2, "0") + " " + 
                String(d.getHours()).padStart(2, "0") + ":00";
              hours.push(key);
            }

            const hourHistory = {};
            const hourLikesCommentsHistory = {};
            
            historyResults.forEach((histRes, index) => {
              if (!histRes) return;
              const records = histRes.data?.post_history_records_list || [];
              
              for (const rec of records) {
                let utcString = rec.record_time;
                if (utcString && !utcString.endsWith("Z")) {
                  utcString = utcString.replace(" ", "T").replace(/\.\d+$/, "") + "Z";
                }
                const d = utcString ? new Date(utcString) : null;
                if (!d || isNaN(d)) continue;
                
                const key = d.getFullYear() + "-" + 
                  String(d.getMonth() + 1).padStart(2, "0") + "-" + 
                  String(d.getDate()).padStart(2, "0") + " " + 
                  String(d.getHours()).padStart(2, "0") + ":00";
                
                const playCount = rec.post_metrics_history?.stats?.play_count ?? 
                                rec.post_metrics_history?.stats?.view_count ?? 0;
                const likes = rec.post_metrics_history?.stats?.like_count ?? 
                            rec.post_metrics_history?.stats?.likes ?? 0;
                const comments = rec.post_metrics_history?.stats?.comment_count ?? 
                               rec.post_metrics_history?.stats?.comments ?? 0;

                if (!hourHistory[key]) hourHistory[key] = 0;
                hourHistory[key] += playCount;
                if (!hourLikesCommentsHistory[key]) hourLikesCommentsHistory[key] = 0;
                hourLikesCommentsHistory[key] += likes + comments;
              }
            });

            let lastValue = 0;
            const hourData = hours.map((key) => {
              if (hourHistory[key] !== undefined) {
                lastValue = hourHistory[key];
              }
              return { date: key, play_count: lastValue };
            });

            let lastLC = 0;
            const hourLikesCommentsData = hours.map((key) => {
              if (hourLikesCommentsHistory[key] !== undefined) {
                lastLC = hourLikesCommentsHistory[key];
              }
              return { date: key, value: lastLC };
            });

            setCachedData(prev => ({ ...prev, hour: hourData }));
            setCachedLikesComments(prev => ({ ...prev, hour: hourLikesCommentsData }));
          } catch (error) {
            console.error("Error calculating hour data:", error);
          }
        })();

        // 计算最新的点赞和评论
        let totalLikes = 0;
        let totalComments = 0;
        historyResults.forEach((histRes) => {
          if (!histRes) return;
          const records = histRes.data?.post_history_records_list || [];
          if (records.length > 0) {
            const latest = records[records.length - 1];
            const likes = latest.post_metrics_history?.stats?.like_count ?? 
                        latest.post_metrics_history?.stats?.likes ?? 0;
            const comments = latest.post_metrics_history?.stats?.comment_count ?? 
                           latest.post_metrics_history?.stats?.comments ?? 0;
            totalLikes += likes;
            totalComments += comments;
          }
        });
        
        setLocalLatestLikes(totalLikes);
        setLocalLatestComments(totalComments);
      } catch (e) {
        setError("Failed to load data, Campaign did not start yet");
        setLocalLatestLikes(null);
        setLocalLatestComments(null);
        setLikesCommentsSeries([]);
        setChartData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [campaignId]);

  // 新增：监听 groupBy 变化，使用缓存数据
  useEffect(() => {
    if (cachedData[groupBy] && cachedLikesComments[groupBy]) {
      setChartData(cachedData[groupBy]);
      setLikesCommentsSeries(cachedLikesComments[groupBy]);
    }
  }, [groupBy, cachedData, cachedLikesComments]);

  // Chart.js
  useEffect(() => {
    if (!chartData || !Array.isArray(chartData) || chartData.length === 0)
      return; // 无数据不画图
    let chart;
    (async () => {
      const Chart = (await import("chart.js/auto")).default;
      // 先移除旧canvas再新建，防止宽度塌陷
      const container = document.querySelector(".div9");
      if (container) {
        let oldCanvas = document.getElementById("reach-performance-chart");
        if (oldCanvas) {
          oldCanvas.remove();
        }
        const newCanvas = document.createElement("canvas");
        newCanvas.id = "reach-performance-chart";
        newCanvas.style.width = "100%";
        newCanvas.style.maxWidth = "100%";
        newCanvas.style.height = "500px";
        newCanvas.height = 500;
        container.appendChild(newCanvas);
      }
      const ctx = document.getElementById("reach-performance-chart");
      if (!ctx) return;
      if (window.reachPerformanceChart) {
        window.reachPerformanceChart.destroy();
      }
      chart = new Chart(ctx, {
        type: "line",
        data: {
          labels: chartData.map((d) => d.date),
          datasets: [
            {
              label: "Total Views",
              data: chartData.map((d) => d.play_count),
              borderColor: "#307fe2",
              backgroundColor: "rgba(48,127,226,0.1)",
              fill: true,
              tension: 0.3,
              pointRadius: 3,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: { padding: 0 },
          plugins: {
            legend: { display: false },
            tooltip: { mode: "index", intersect: false },
          },
          scales: {
            x: {
              title: {
                display: true,
                text: groupBy === "hour" ? "Hour" : "Date",
              },
            },
            y: {
              title: { display: true, text: "Total Views" },
              beginAtZero: true,
            },
          },
        },
      });
      window.reachPerformanceChart = chart;
    })();
    return () => {
      if (window.reachPerformanceChart) {
        window.reachPerformanceChart.destroy();
        window.reachPerformanceChart = null;
      }
    };
  }, [chartData, groupBy]);

  // 新增：将最新数据点的record_time传递给父组件
  useEffect(() => {
    if (Array.isArray(chartData) && chartData.length > 0) {
      // 取最后一个数据点的date作为record_time
      setLatestRecordTime &&
        setLatestRecordTime(chartData[chartData.length - 1].date);
    } else {
      setLatestRecordTime && setLatestRecordTime(null);
    }
  }, [chartData, setLatestRecordTime]);

  // 格式化views为1.0M等
  function formatViews(val) {
    if (val === undefined || val === null) return "-";
    const num = Number(val);
    let formatted;
    if (num >= 1e9) {
      formatted = (num / 1e9).toFixed(1);
      if (formatted.endsWith(".0")) formatted = formatted.slice(0, -2);
      return formatted + " B";
    } else if (num >= 1e6) {
      formatted = (num / 1e6).toFixed(1);
      if (formatted.endsWith(".0")) formatted = formatted.slice(0, -2);
      return formatted + " M";
    } else if (num >= 1e3) {
      formatted = (num / 1e3).toFixed(1);
      if (formatted.endsWith(".0")) formatted = formatted.slice(0, -2);
      return formatted + " K";
    } else {
      return Math.round(num).toString();
    }
  }

  // 计算最新的 views 总和
  const latestViews =
    chartData && chartData.length > 0
      ? chartData[chartData.length - 1].play_count
      : null;

  // 计算环比增长百分比
  let percentChange = null;
  if (chartData && chartData.length > 1) {
    const latest = chartData[chartData.length - 1].play_count;
    const prev = chartData[chartData.length - 2].play_count;
    if (prev > 0) {
      percentChange = ((latest - prev) / prev) * 100;
    } else if (latest > 0) {
      percentChange = 100;
    } else {
      percentChange = 0;
    }
  }

  // 计算 likes+comments 的环比增长百分比
  let likesCommentsPercentChange = null;
  if (likesCommentsSeries && likesCommentsSeries.length > 1) {
    const latest = likesCommentsSeries[likesCommentsSeries.length - 1].value;
    const prev = likesCommentsSeries[likesCommentsSeries.length - 2].value;
    if (prev > 0) {
      likesCommentsPercentChange = ((latest - prev) / prev) * 100;
    } else if (latest > 0) {
      likesCommentsPercentChange = 100;
    } else {
      likesCommentsPercentChange = 0;
    }
  }

  // 最新 engagement rate（动态计算，保留一位小数）
  const engagementRate =
    latestViews &&
    localLatestLikes !== null &&
    localLatestComments !== null &&
    latestViews > 0
      ? ((localLatestLikes + localLatestComments) / latestViews) * 100
      : 0;
  // Engagement progress bar width: (engagementRate / 5) * 100%, capped at 100%
  const engagementBarWidth = Math.max(
    0,
    Math.min((engagementRate / 5) * 100, 100),
  );

  // 通知父组件最新的 currentViews
  useEffect(() => {
    if (typeof setCurrentViews === "function") {
      setCurrentViews(latestViews);
    }
    if (typeof setLatestViews === "function") {
      setLatestViews(latestViews);
    }
    if (typeof setPercentChange === "function") {
      setPercentChange(percentChange);
    }
    if (typeof setLatestLikes === "function") {
      setLatestLikes(localLatestLikes);
    }
    if (typeof setLatestComments === "function") {
      setLatestComments(localLatestComments);
    }
    if (typeof setLikesCommentsPercentChange === "function") {
      setLikesCommentsPercentChange(likesCommentsPercentChange);
    }
    if (typeof setEngagementRate === "function") {
      setEngagementRate(engagementRate);
    }
  }, [
    latestViews,
    percentChange,
    localLatestLikes,
    localLatestComments,
    likesCommentsPercentChange,
    engagementRate,
    setCurrentViews,
    setLatestViews,
    setPercentChange,
    setLatestLikes,
    setLatestComments,
    setLikesCommentsPercentChange,
    setEngagementRate,
  ]);

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;700&family=Lato:wght@400;700&display=swap"
        rel="stylesheet"
      />
      <section className={styles.performanceContainer}>
        <h1 className={styles.performanceTitle}>Performance Analytics</h1>
        <div className={styles.performanceContent}>
          {(!chartData ||
            (Array.isArray(chartData) && chartData.length === 0)) &&
          !loading &&
          !error ? (
            <div style={{ width: "100%", minHeight: 300 }}></div>
          ) : error === "Failed to load data, Campaign did not start yet" ? (
            <div
              style={{
                width: "100%",
                minHeight: 300,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#888",
                background: "#f8fafd",
                borderRadius: 16,
                border: "1px solid #e0e0e0",
                margin: "40px 0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              }}
            >
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                style={{ marginBottom: 16 }}
              >
                <circle cx="12" cy="12" r="10" fill="#eaf2ff" />
                <path
                  d="M12 8v4"
                  stroke="#307fe2"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <circle cx="12" cy="16" r="1" fill="#307fe2" />
              </svg>
              <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
                Campaign has not started yet
              </div>
              <div style={{ fontSize: 16, color: "#aaa" }}>
                No analytics data available until the campaign begins.
              </div>
            </div>
          ) : (
            <div className="div">
              <div className="div2">
                {/* 移除内层的Performance Analytics标题 */}
              </div>

              <div className="div8">
                <div className="reachPerformance">Reach Performance </div>
                <div className="dailyviewsandreachmetrics">
                  Daily views and reach metrics
                </div>
                <div style={{ margin: "10px 0 10px 0" }}>
                  <label style={{ fontWeight: 500, marginRight: 8 }}>
                    Group by:
                  </label>
                  <select
                    value={groupBy}
                    onChange={(e) => setGroupBy(e.target.value)}
                    style={{ fontSize: 15, padding: "2px 8px" }}
                  >
                    <option value="day">Day</option>
                    <option value="hour">Hour</option>
                  </select>
                </div>
                <div
                  className="div9"
                  style={{
                    position: "relative",
                    background: "#fff",
                    width: "100%",
                    minHeight: 500,
                    maxWidth: "100%",
                    overflowX: "auto",
                  }}
                >
                  {loading && (
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: 0,
                        bottom: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 2,
                      }}
                    >
                      Loading...
                    </div>
                  )}
                  {error && (
                    <div style={{ color: "red", padding: 20 }}>{error}</div>
                  )}
                  {/* 只有有数据时才会插入canvas */}
                </div>
              </div>
              <div className="div10"></div>
              <div className="bottomStatsRow">
                <div className="div14">
                  <div className="div15">
                    <img
                      src="https://cdn.builder.io/api/v1/image/assets/TEMP/b37cfca834770c2cfd13b2804cef53589d5ab0ff?placeholderIfAbsent=true&apiKey=dc2f6a1e66164bfb9d194f96d43a57dd"
                      className="img4"
                    />
                    <div className="reachProgress">Reach Progress</div>
                  </div>
                  <div className="css800">
                    {latestViews !== null && targetViews
                      ? ((latestViews / targetViews) * 100).toFixed(1) + "%"
                      : "-"}
                  </div>
                  <div className="views">{formatViews(targetViews)} views</div>
                  <div className="rectangle">
                    <div
                      className="rectangle2"
                      style={{
                        width:
                          latestViews !== null && targetViews
                            ? Math.min((latestViews / targetViews) * 100, 100) +
                              "%"
                            : "0%",
                      }}
                    />
                  </div>
                </div>
                <div className="div18">
                  <div className="div19">
                    <img
                      src="https://cdn.builder.io/api/v1/image/assets/TEMP/eaf33a1e287dd903402ece8733766783bcf2f166?placeholderIfAbsent=true&apiKey=dc2f6a1e66164bfb9d194f96d43a57dd"
                      className="img5"
                    />
                    <div className="reachProgress">Engagement Rate</div>
                  </div>
                  <div className="div20">
                    <div className="div21">
                      <div className="css8002">
                        {latestViews &&
                        localLatestLikes !== null &&
                        localLatestComments !== null &&
                        latestViews > 0
                          ? (
                              ((localLatestLikes + localLatestComments) /
                                latestViews) *
                              100
                            ).toFixed(1) + "%"
                          : "-"}
                      </div>
                      <div className="target25">Target: 2.5%</div>
                    </div>
                    <div className="css5">5%</div>
                  </div>
                  <div
                    className="rectangle3"
                    style={{
                      height: 8,
                      marginTop: 6,
                      position: "relative",
                      background: "#e9e9e9",
                      borderRadius: 100,
                    }}
                  >
                    <div
                      className="rectangle4"
                      style={{
                        width: engagementBarWidth.toFixed(1) + "%",
                        height: 8,
                        background: "#307fe2",
                        borderRadius: 100,
                        position: "relative",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export default PerformanceAnalytics;
